import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Conversation, ConversationMessage, ConversationMessageType } from 'app/models/conversation.model';
import { ConversationService } from 'app/services/conversation.service';
import { AudioPlayerComponent } from 'app/modules/ambulance/components/ambulance-chat/components/audio-player/audio-player.component';
import {
    PhotoLightboxDialogComponent,
    PhotoLightboxDialogData,
} from 'app/shared/components/photo-lightbox-dialog/photo-lightbox-dialog.component';
import { AutoTranslateDirective } from 'app/shared/directives/auto-translate.directive';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';
import { TimezoneDatePipe } from 'app/shared/pipes/timezone-date.pipe';
import { Subject, take, takeUntil } from 'rxjs';
import { finalize } from 'rxjs/operators';

declare let MicRecorder: any;

@Component({
    selector: 'app-conversation-thread',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatMenuModule,
        MatTooltipModule,
        AudioPlayerComponent,
        AutoTranslateDirective,
        AutoTranslatePipe,
        TimezoneDatePipe,
    ],
    templateUrl: './conversation-thread.component.html',
    styleUrls: ['./conversation-thread.component.scss'],
})
export class ConversationThreadComponent implements OnInit, OnChanges, OnDestroy {
    @Input() conversation: Conversation | null = null;
    @Input() currentUserId: number | null = null;
    @Input() currentUserName: string | null = null;
    @Input() showBack = true;
    @Input() showHeader = true;
    @Input() showVideoButton = true;
    @Input() embedded = false;

    @Output() back = new EventEmitter<void>();
    @Output() enterVideo = new EventEmitter<Conversation>();

    @ViewChild('scrollContainer') private scrollContainer: ElementRef;
    @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
    @ViewChild('cameraInput') cameraInput: ElementRef<HTMLInputElement>;
    @ViewChild('composerInput') composerInput?: ElementRef<HTMLTextAreaElement>;

    private readonly composerMaxLines = 4;

    messages: ConversationMessage[] = [];
    newMessageText = '';
    isRecording = false;
    recordingDuration = 0;
    private recordingInterval: any;
    private recorder: any;
    private destroy$ = new Subject<void>();
    private objectUrls: string[] = [];
    /** Evita change duplicado do input file (comum ao abrir picker via mat-menu). */
    private attachInFlight = false;
    private lastAttachKey: string | null = null;
    private lastAttachAt = 0;

    constructor(
        private readonly conversationService: ConversationService,
        private readonly dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.conversationService.messages$.pipe(takeUntil(this.destroy$)).subscribe((msgs) => {
            this.messages = msgs;
            setTimeout(() => this.scrollToBottom(), 30);
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['conversation'] && this.conversation?.conversation_id) {
            this.openConversation(this.conversation.conversation_id);
        }
    }

    ngOnDestroy(): void {
        this.stopRecordingTimer();
        this.revokeObjectUrls();
        this.destroy$.next();
        this.destroy$.complete();
    }

    openConversation(conversationId: number): void {
        this.conversationService.joinConversation(conversationId);
        this.conversationService.loadMessages(conversationId, 1, 50).pipe(take(1)).subscribe();
    }

    onBack(): void {
        this.conversationService.leaveConversation();
        this.back.emit();
    }

    onEnterVideo(): void {
        if (this.conversation) {
            this.enterVideo.emit(this.conversation);
        }
    }

    onComposerKeydown(event: KeyboardEvent): void {
        if (event.key !== 'Enter' || event.shiftKey) {
            return;
        }
        event.preventDefault();
        this.sendMessage();
    }

    resizeComposer(): void {
        const el = this.composerInput?.nativeElement;
        if (!el) return;

        const lineHeight = this.composerLineHeight(el);
        const padding = this.composerVerticalPadding(el);
        const maxHeight = lineHeight * this.composerMaxLines + padding;

        el.style.height = 'auto';
        const scrollHeight = el.scrollHeight;
        const next = Math.min(scrollHeight, maxHeight);
        el.style.height = `${next}px`;
        el.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
    }

    private resetComposerHeight(): void {
        const el = this.composerInput?.nativeElement;
        if (!el) return;
        el.style.height = '';
        el.style.overflowY = '';
        requestAnimationFrame(() => this.resizeComposer());
    }

    private composerLineHeight(el: HTMLTextAreaElement): number {
        const parsed = parseFloat(getComputedStyle(el).lineHeight);
        return Number.isFinite(parsed) ? parsed : 22;
    }

    private composerVerticalPadding(el: HTMLTextAreaElement): number {
        const style = getComputedStyle(el);
        return parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    }

    sendMessage(): void {
        const text = this.newMessageText?.replace(/^\s+|\s+$/g, '');
        const ctx = this.sendContext();
        if (!text || !this.conversation || !ctx) return;

        this.newMessageText = '';
        this.resetComposerHeight();
        this.conversationService.sendText(this.conversation.conversation_id, text, ctx).pipe(take(1)).subscribe();
    }

    triggerFileInput(event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        // Aguarda o mat-menu fechar antes de abrir o picker (evita double-change no mobile).
        setTimeout(() => this.fileInput?.nativeElement?.click(), 150);
    }

    triggerCameraInput(event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        setTimeout(() => this.cameraInput?.nativeElement?.click(), 150);
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        const ctx = this.sendContext();
        if (!file || !this.conversation || !ctx) {
            input.value = '';
            return;
        }

        const attachKey = `${file.name}|${file.size}|${file.lastModified}`;
        const now = Date.now();
        if (this.attachInFlight || (this.lastAttachKey === attachKey && now - this.lastAttachAt < 2500)) {
            input.value = '';
            return;
        }

        this.attachInFlight = true;
        this.lastAttachKey = attachKey;
        this.lastAttachAt = now;

        const preview = this.buildFilePreview(file);
        this.conversationService
            .sendMedia(this.conversation.conversation_id, file, ctx, preview)
            .pipe(
                take(1),
                finalize(() => {
                    this.attachInFlight = false;
                }),
            )
            .subscribe({
                error: () => {
                    /* finalize já libera o lock */
                },
            });
        input.value = '';
    }

    startRecording(): void {
        if (typeof MicRecorder === 'undefined') return;
        this.recorder = new MicRecorder({ bitRate: 128 });
        this.recorder
            .start()
            .then(() => {
                this.isRecording = true;
                this.recordingDuration = 0;
                this.recordingInterval = setInterval(() => this.recordingDuration++, 1000);
            })
            .catch(() => {
                this.isRecording = false;
            });
    }

    cancelRecording(): void {
        this.stopRecordingTimer();
        if (this.recorder) {
            this.recorder.stop();
        }
        this.isRecording = false;
    }

    sendAudio(): void {
        const ctx = this.sendContext();
        if (!this.recorder || !this.conversation || !ctx) return;
        const duration = this.recordingDuration;
        this.stopRecordingTimer();
        this.recorder
            .stop()
            .getMp3()
            .then(([buffer, blob]: [any, Blob]) => {
                this.isRecording = false;
                const file = new File(buffer, `audio-${Date.now()}.mp3`, {
                    type: blob.type || 'audio/mpeg',
                    lastModified: Date.now(),
                });
                const blobUrl = URL.createObjectURL(blob);
                this.objectUrls.push(blobUrl);
                this.conversationService
                    .sendMedia(
                        this.conversation!.conversation_id,
                        file,
                        ctx,
                        {
                            type: 'AUDIO',
                            file_url: blobUrl,
                            file_name: file.name,
                        },
                        { duration_seconds: duration },
                    )
                    .pipe(take(1))
                    .subscribe();
            })
            .catch(() => {
                this.isRecording = false;
            });
    }

    formatDuration(seconds: number): string {
        const m = Math.floor(seconds / 60)
            .toString()
            .padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    }

    isMine(msg: ConversationMessage): boolean {
        return !!this.currentUserId && msg.user_id === this.currentUserId;
    }

    resendMessage(msg: ConversationMessage): void {
        const ctx = this.sendContext();
        if (!msg?.failed || !msg.temp_id || !ctx) return;
        this.conversationService.resend(msg.temp_id, ctx).pipe(take(1)).subscribe();
    }

    shouldShowDay(previous: ConversationMessage | undefined, current: ConversationMessage): boolean {
        if (!previous || previous.type === 'SYSTEM') return true;
        return this.dayKey(previous.createdAt) !== this.dayKey(current.createdAt);
    }

    isImageAttachment(msg: ConversationMessage): boolean {
        if (msg.type === 'IMAGE') return !!msg.file_url;
        if (msg.type !== 'FILE' || !msg.file_url) return false;
        const name = String(msg.file_name || msg.content || msg.file_url).toLowerCase();
        return /\.(png|jpe?g|gif|webp|bmp|heic|heif|svg)(\?|$)/i.test(name);
    }

    openImageLightbox(msg: ConversationMessage, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        if (!msg?.file_url) return;

        const photos = this.messages
            .filter((m) => m.file_url && (m.type === 'IMAGE' || this.isImageAttachment(m)))
            .map((m) => ({ path: m.file_url!, name: m.file_name || m.content || null }));

        if (!photos.length) {
            photos.push({ path: msg.file_url, name: msg.file_name || msg.content || null });
        }

        let initialIndex = photos.findIndex((p) => p.path === msg.file_url);
        if (initialIndex < 0) initialIndex = 0;

        const data: PhotoLightboxDialogData = { photos, initialIndex };
        this.dialog.open(PhotoLightboxDialogComponent, {
            data,
            maxWidth: '100vw',
            maxHeight: '100dvh',
            panelClass: 'photo-lightbox-panel',
            autoFocus: false,
        });
    }

    private dayKey(value: string | Date | null | undefined): string {
        if (!value) return '';
        const date = new Date(value);
        if (!Number.isFinite(date.getTime())) return '';
        return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    }

    private sendContext(): { user_id: number; user_name: string } | null {
        if (!this.currentUserId) return null;
        return {
            user_id: this.currentUserId,
            user_name: this.currentUserName || 'Você',
        };
    }

    private buildFilePreview(file: File): {
        type: ConversationMessageType;
        file_url?: string;
        file_name: string;
    } {
        const mime = (file.type || '').toLowerCase();
        const name = file.name;
        if (mime.startsWith('image/')) {
            const url = URL.createObjectURL(file);
            this.objectUrls.push(url);
            return { type: 'IMAGE', file_url: url, file_name: name };
        }
        if (mime.startsWith('audio/')) {
            const url = URL.createObjectURL(file);
            this.objectUrls.push(url);
            return { type: 'AUDIO', file_url: url, file_name: name };
        }
        return { type: 'FILE', file_name: name };
    }

    private scrollToBottom(): void {
        try {
            const el = this.scrollContainer?.nativeElement;
            if (el) el.scrollTop = el.scrollHeight;
        } catch {
            // ignore
        }
    }

    private stopRecordingTimer(): void {
        if (this.recordingInterval) {
            clearInterval(this.recordingInterval);
            this.recordingInterval = null;
        }
    }

    private revokeObjectUrls(): void {
        for (const url of this.objectUrls) {
            URL.revokeObjectURL(url);
        }
        this.objectUrls = [];
    }
}
